/* Adaptable mode: the vaults the AI sells from, and every item in them with its
   stage. Stage picks save straight away (per item, or in bulk from Select mode).
   "AI analyse" describes an item — which also sets its stage — and uses one
   credit per item. (No per-item bundle/standalone format: the brain derives a
   rung's format from its item count.) */

import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../../../../api/client';
import type { OfVault, OnDemandVault, SalesAttachedVault, SalesMediaItem } from '../../../../api/client';
import { VaultThumb } from '../../Sales/VaultThumb';
import { fmtDuration, isVideoItem } from '../../SalesLadder/MediaBadge';
import { Btn, Card, ErrorLine, Icon, Pill, RunTag, Scan, Seg, Spin, Toggle, Warn, XButton } from '../kit';
import { Confirm } from '../content/VaultsSection';
import { VaultViewer } from '../content/VaultViewer';
import { font } from '../style';
import { C } from '../tokens';
import { CheckBox, Pick, type PickOpt } from './Pick';
import { FoldHeader } from './StagesSection';
import { canDescribe, isDescribed, type PoolItem } from './model';
import type { SalesPool } from './useSalesPool';

const SHOW = 24;

const thumbOf = (it: SalesMediaItem) => it.thumb_url || it.preview_url || (isVideoItem(it.type) ? null : it.full_url);

/** Ask before spending more credits than are left. */
function useCreditGate(pool: SalesPool) {
  const { t } = useTranslation('assistantCompact');
  const [ask, setAsk] = useState<{ its: PoolItem[]; name: string } | null>(null);
  const go = (its: PoolItem[], name: string) => {
    const n = its.filter(canDescribe).length;
    if (!n) return;
    if (pool.credits !== null && n > pool.credits) setAsk({ its, name });
    else pool.describe(its);
  };
  const dialog = ask && (
    <Confirm
      title={ask.name}
      body={pool.credits !== null && pool.credits <= 0 ? t('content.noCredits') : t('sl.ct.shortCredits', { count: ask.its.filter(canDescribe).length, credits: pool.credits ?? 0 })}
      canGo={(pool.credits ?? 0) > 0}
      goLabel={t('sl.ct.analyseN', { count: pool.credits ?? 0 })}
      onGo={() => { pool.describe(ask.its.filter(canDescribe).slice(0, pool.credits ?? 0)); setAsk(null); }}
      onClose={() => setAsk(null)}
    />
  );
  return { go, dialog };
}

const asVault = (v: SalesAttachedVault, n: number): OnDemandVault => ({ id: v.id, vault_name: v.vault_name, total_items: n, classified_items: 0, needs_review_items: 0 });

/* ── Vaults ─────────────────────────────────────────────────────── */

export function VaultsBlock({ accountId, pool, open, onToggle, one, onOne }: { accountId: string; pool: SalesPool; open: boolean; onToggle: () => void; one: boolean; onOne: (v: boolean) => void }) {
  const { t } = useTranslation('assistantCompact');
  const [menu, setMenu] = useState(false);
  const [all, setAll] = useState<OfVault[] | null>(null);
  const [syncing, setSyncing] = useState(false);
  const [view, setView] = useState<SalesAttachedVault | null>(null);
  const gate = useCreditGate(pool);

  useEffect(() => {
    if (!menu || all) return;
    api.getVaultsForAccount(accountId).then(setAll, () => setAll([]));
  }, [menu, all, accountId]);
  const sync = () => {
    setSyncing(true);
    api.syncAccountVaults(accountId).then(setAll, () => {}).finally(() => setSyncing(false));
  };
  const ids = new Set(pool.attached.map((v) => v.id));
  const options = (all ?? []).filter((v) => !ids.has(v.id));

  return (
    <div data-tutorial="sl-vaults" style={{ display: 'flex', flexDirection: 'column', gap: 8, minWidth: 0 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', minHeight: 28 }}>
        <FoldHeader open={open} onToggle={onToggle} title={t('sl.vt.title')}>
          <span style={{ font: font(400, 12), color: C.muted }}>{pool.attached.length}</span>
          <span style={{ font: font(400, 11.5), color: C.muted, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', minWidth: 0 }}>{t('sl.vt.hint')}</span>
        </FoldHeader>
        {pool.credits !== null && <Pill>{t('content.credits', { count: pool.credits })}</Pill>}
        <div title={one ? t('sl.vt.oneOn') : t('sl.vt.oneOff')} style={{ display: 'flex', alignItems: 'center', gap: 8, height: 28, padding: '0 10px', border: `1px solid ${C.border}`, borderRadius: 6, background: C.card }}>
          <span style={{ font: font(500, 12), color: 'var(--om-body)', whiteSpace: 'nowrap' }}>{t('sl.vt.one')}</span>
          <Toggle on={one} onChange={onOne} />
        </div>
        <div style={{ position: 'relative' }}>
          <Btn kind="tint" icon="plus" onClick={() => setMenu((m) => !m)}>{t('sl.vt.attach')}</Btn>
          {menu && (
            <>
              <div onClick={() => setMenu(false)} style={{ position: 'fixed', inset: 0, zIndex: 9 }} />
              <div style={{ position: 'absolute', top: 34, right: 0, zIndex: 10, width: 260, background: C.card, border: `1px solid ${C.borderHi}`, borderRadius: 10, boxShadow: '0 12px 32px rgba(0,0,0,.5)', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                <div className="cpt-thin-scroll" style={{ maxHeight: 260, overflow: 'auto', padding: 4, display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {!all && <div style={{ padding: 14, display: 'flex', justifyContent: 'center', color: C.muted }}><Spin /></div>}
                  {options.map((o) => (
                    <button key={o.id} type="button" className="cpt-hover-row" onClick={() => { pool.attach(o); setMenu(false); }} style={{ height: 32, flex: 'none', padding: '0 8px', border: 0, borderRadius: 6, background: 'transparent', display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', textAlign: 'left' }}>
                      <Icon name="folder" size={13} color={C.sub} />
                      <span style={{ flex: 1, minWidth: 0, font: font(500, 12.5), color: C.text, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{o.vault_name || o.id}</span>
                    </button>
                  ))}
                  {all && !options.length && <div style={{ padding: 14, textAlign: 'center', font: font(400, 12), color: C.muted }}>{t('sl.vt.allAttached')}</div>}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', padding: '6px 8px', borderTop: `1px solid ${C.divider}` }}>
                  <Btn kind="text" height={26} disabled={syncing} onClick={sync} title={t('content.refreshTip')} style={{ color: C.sub }}>{syncing ? <Spin /> : <Icon name="refresh-cw" size={12} />}{t('content.refresh')}</Btn>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
      {pool.error && <ErrorLine>{pool.error}</ErrorLine>}
      {open && (
        <Card>
          {pool.attached.map((v) => {
            const its = pool.items[v.id] ?? [];
            const loading = pool.loadingVaults.has(v.id);
            const run = its.some((i) => pool.analysing.has(i.id));
            const done = its.filter(isDescribed).length;
            const todo = its.filter(canDescribe).length;
            const w = its.length ? Math.round((done / its.length) * 100) : 0;
            return (
              <div key={v.id} className="cpt-row" style={{ display: 'flex', alignItems: 'center', gap: 10, minHeight: 44, padding: '0 12px' }}>
                <div style={{ flex: 1, minWidth: 80, display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span onClick={() => setView(v)} style={{ font: font(500, 13), color: C.text, cursor: 'pointer', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{v.vault_name || v.id}</span>
                  {run && <RunTag>{t('content.analysing')}</RunTag>}
                </div>
                <div style={{ width: 48, flex: 'none', height: 4, borderRadius: 99, background: C.track, overflow: 'hidden' }}>
                  <div style={{ height: 4, width: `${w}%`, background: run ? C.purpleLt : C.purple, transition: 'width .25s linear' }} />
                </div>
                <span style={{ font: font(400, 12), color: C.sub, flex: 'none', whiteSpace: 'nowrap', minWidth: 60 }}>{loading && !its.length ? <Spin /> : t('content.nOf', { n: done, total: its.length })}</span>
                <Btn kind="ghost" height={26} onClick={() => setView(v)}>{t('content.view')}</Btn>
                <Btn kind="ghost" height={26} disabled={run || loading || !todo} onClick={() => gate.go(its, v.vault_name)} title={todo ? t('sl.vt.analyseTip') : t('content.allAnalysedTip')} style={{ color: C.purplePale }}>{run ? t('sl.analysingDots') : todo ? t('content.analyse') : t('content.analysed')}</Btn>
                <XButton onClick={() => pool.detach(v.id)} title={t('sl.vt.detach')} />
              </div>
            );
          })}
          {!pool.attached.length && !pool.attachedLoading && <div style={{ padding: '12px 14px' }}><Warn>{t('sl.vt.none')}</Warn></div>}
          {!pool.attached.length && pool.attachedLoading && <div style={{ padding: '12px 14px', color: C.muted }}><Spin /></div>}
        </Card>
      )}
      {gate.dialog}
      {view && <VaultViewer accountId={accountId} vault={asVault(view, (pool.items[view.id] ?? []).length)} onClose={() => setView(null)} onChanged={() => {}} onCredits={() => {}} />}
    </div>
  );
}

/* ── Content grid ───────────────────────────────────────────────── */

export interface StageOpt { key: string; name: string; sub: string }

export function ContentBlock({ accountId, pool, stages }: { accountId: string; pool: SalesPool; stages: StageOpt[] }) {
  const { t } = useTranslation('assistantCompact');
  const [vf, setVf] = useState('');
  const [sf, setSf] = useState<string>('all');
  const [shown, setShown] = useState(SHOW);
  const [selMode, setSelMode] = useState(false);
  const [sel, setSel] = useState<string[]>([]);
  const [view, setView] = useState<{ v: SalesAttachedVault; id: string } | null>(null);
  const gate = useCreditGate(pool);

  const keys = useMemo(() => new Set(stages.map((s) => s.key)), [stages]);
  const all = useMemo(() => pool.attached.flatMap((v) => pool.items[v.id] ?? []), [pool.attached, pool.items]);
  const stOf = (it: PoolItem) => (it.sales_stage && keys.has(it.sales_stage) ? it.sales_stage : '');
  const inc = (it: PoolItem) => !stOf(it);
  const un = all.filter(inc).length;
  const fl = all.filter((it) => (!vf || it.vault_id === vf) && (sf === 'all' || (sf === 'un' ? inc(it) : stOf(it) === sf)));
  const flIds = fl.map((i) => i.id);
  const allOn = flIds.length > 0 && flIds.every((id) => sel.includes(id));
  const selItems = all.filter((i) => sel.includes(i.id));
  const nAn = selItems.filter((i) => canDescribe(i) && !pool.analysing.has(i.id)).length;
  const loading = pool.loadingVaults.size > 0;
  const reset = () => setShown(SHOW);

  const stageOpts: PickOpt<string>[] = stages.map((s) => ({ v: s.key, label: s.name, sub: s.sub }));
  const sfOpts: PickOpt<string>[] = [
    { v: 'all', label: t('sl.ct.allStages') },
    { v: 'un', label: t('sl.ct.incomplete'), sub: t('sl.ct.incompleteSub', { count: un }) },
    ...stageOpts,
  ];
  const sfLabel = sf === 'all' ? t('sl.ct.allStages') : sf === 'un' ? `${t('sl.ct.incomplete')} · ${un}` : stages.find((s) => s.key === sf)?.name ?? t('sl.ct.allStages');

  if (!pool.attached.length) return null;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap' }}>
        <span style={{ font: font(600, 13), color: C.text }}>{t('sl.ct.title')}</span>
        <span style={{ font: font(400, 12), color: C.muted }}>{t('sl.nItems', { count: all.length })}</span>
        {loading && <Spin size={12} />}
        <span style={{ font: font(400, 11.5), color: C.muted }}>{t('sl.ct.hint')}</span>
      </div>
      {un > 0 && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: -4 }}>
          <Warn>{t('sl.ct.missing', { count: un })}</Warn>
          <Btn kind="text" height={22} onClick={() => { setSf('un'); reset(); }} style={{ color: C.purpleLt, padding: '0 6px' }}>{t('sl.ct.showThem')}</Btn>
        </div>
      )}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', minHeight: 32, padding: '2px 0' }}>
        {!selMode ? (
          <>
            <Seg value={vf} onChange={(v) => { setVf(v); reset(); }} options={[{ id: '', label: t('sl.ct.all') }, ...pool.attached.map((v) => ({ id: v.id, label: v.vault_name }))]} />
            <Pick label={sfLabel} icon="filter" value={sf} options={sfOpts} onPick={(v) => { setSf(v); reset(); }} title={t('sl.ct.filterTip')} />
            <span style={{ flex: 1 }} />
            <Btn kind="ghost" icon="check-square" onClick={() => { setSelMode(true); setSel([]); }}>{t('sl.ct.select')}</Btn>
          </>
        ) : (
          <>
            <button type="button" onClick={() => setSel((s) => (allOn ? s.filter((k) => !flIds.includes(k)) : [...new Set([...s, ...flIds])]))} style={{ height: 28, flex: 'none', display: 'flex', alignItems: 'center', gap: 8, padding: '0 10px 0 8px', background: 'color-mix(in srgb, var(--om-accent) 12%, transparent)', border: `1px solid ${C.tintLine45}`, borderRadius: 6, color: C.text, font: font(600, 12), cursor: 'pointer', whiteSpace: 'nowrap' }}>
              <CheckBox state={allOn ? 'on' : sel.length ? 'some' : 'off'} />{t('sl.ct.nSelected', { count: sel.length })}
            </button>
            <Btn kind="text" onClick={() => setSel((s) => (allOn ? s.filter((k) => !flIds.includes(k)) : [...new Set([...s, ...flIds])]))} style={{ color: C.purpleLt, padding: '0 6px' }}>{allOn ? t('picker.deselectAll') : t('picker.selectAll', { count: flIds.length })}</Btn>
            <span style={{ flex: 1 }} />
            <Pick label={t('sl.ct.setStage')} icon="bar-chart-2" options={[...stageOpts, { v: '', label: t('sl.ct.removeStage'), rm: true }]} onPick={(v) => pool.setField(selItems, { sales_stage: v || null })} disabled={!sel.length} align="right" menuWidth={180} />
            <Btn kind="ghost" icon="delete" disabled={!sel.length} onClick={() => pool.setField(selItems, { sales_stage: null })}>{t('sl.ct.clear')}</Btn>
            <Btn kind="tint" icon="sparkles" disabled={!nAn} title={nAn ? t('sl.ct.analyseTip', { count: nAn }) : t('sl.ct.allAnalysed')} onClick={() => gate.go(selItems.filter((i) => !pool.analysing.has(i.id)), t('sl.ct.selectedItems', { count: nAn }))}>{nAn ? t('sl.ct.aiAnalyseN', { count: nAn }) : t('sl.ct.aiAnalyse')}</Btn>
            <Btn kind="text" onClick={() => { setSelMode(false); setSel([]); }} style={{ color: C.sub }}>{t('sl.ct.done')}</Btn>
          </>
        )}
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(176px,1fr))', gap: 8 }}>
        {fl.slice(0, shown).map((it) => {
          const on = sel.includes(it.id);
          const ing = pool.analysing.has(it.id);
          const st = stOf(it);
          const vid = isVideoItem(it.type);
          const v = pool.attached.find((x) => x.id === it.vault_id)!;
          const stName = st ? stages.find((s) => s.key === st)!.name : it.sales_stage ? it.sales_stage : t('sl.ct.setStage');
          return (
            <div key={it.id} style={{ display: 'flex', flexDirection: 'column', background: C.card, border: `1px solid ${on ? C.purple : C.border}`, borderRadius: 8, boxShadow: on ? `0 0 0 1px ${C.purple}` : 'none' }}>
              <div onClick={() => (selMode ? setSel((s) => (on ? s.filter((k) => k !== it.id) : [...s, it.id])) : setView({ v, id: it.id }))} title={`${vid ? t('sl.ct.video') : t('sl.ct.photo')} · ${it.vault_name}`} style={{ position: 'relative', aspectRatio: '4/3', borderRadius: '7px 7px 0 0', overflow: 'hidden', background: C.stripe, cursor: 'pointer' }}>
                <VaultThumb accountId={accountId} url={thumbOf(it)} className="w-full h-full" />
                {ing && <Scan />}
                {selMode && (
                  <span style={{ position: 'absolute', left: 6, top: 6, width: 18, height: 18, boxSizing: 'border-box', borderRadius: 5, border: `1.5px solid ${on ? C.purple : 'color-mix(in srgb, var(--om-text) 75%, transparent)'}`, background: on ? C.purple : 'color-mix(in srgb, var(--om-page) 55%, transparent)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', zIndex: 2 }}>{on && <Icon name="check" size={12} />}</span>
                )}
                {vid && <span style={{ position: 'absolute', left: 6, bottom: 6, display: 'flex', alignItems: 'center', gap: 3, padding: '1px 5px', borderRadius: 4, background: 'color-mix(in srgb, var(--om-page) 72%, transparent)', font: font(600, 10.5), color: C.text, zIndex: 2 }}><Icon name="play" size={9} />{fmtDuration(it.duration)}</span>}
                <span style={{ position: 'absolute', right: 6, bottom: 6, maxWidth: '55%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', padding: '1px 5px', borderRadius: 4, background: 'color-mix(in srgb, var(--om-page) 72%, transparent)', font: font(500, 10.5), color: C.sub, zIndex: 2 }}>{it.vault_name}</span>
                {ing && <span style={{ position: 'absolute', left: '50%', top: '50%', transform: 'translate(-50%,-50%)', display: 'flex', alignItems: 'center', gap: 5, padding: '3px 9px', borderRadius: 99, background: 'color-mix(in srgb, var(--om-page) 80%, transparent)', font: font(500, 11), color: C.purplePale, whiteSpace: 'nowrap', pointerEvents: 'none', zIndex: 2 }}><Spin size={11} />{t('content.analysing')}</span>}
                {!ing && canDescribe(it) && (
                  <button type="button" title={t('sl.ct.analyseOneTip')} onClick={(e) => { e.stopPropagation(); gate.go([it], t('sl.ct.selectedItems', { count: 1 })); }} style={{ position: 'absolute', left: '50%', top: '50%', transform: 'translate(-50%,-50%)', height: 28, display: 'flex', alignItems: 'center', gap: 5, padding: '0 11px', background: 'color-mix(in srgb, var(--om-card) 90%, transparent)', border: '1px solid color-mix(in srgb, var(--om-accent) 70%, transparent)', borderRadius: 99, color: C.text, font: font(500, 12), cursor: 'pointer', whiteSpace: 'nowrap', boxShadow: '0 4px 14px rgba(0,0,0,.4)', zIndex: 2 }}>
                    <Icon name="sparkles" size={12} color={C.purplePale} />{t('sl.ct.aiAnalyse')}
                  </button>
                )}
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6, padding: 8 }}>
                <Pick full height={26} menuWidth={176} title={t('sl.ct.stage')} label={stName} icon="bar-chart-2" warn={!st} value={st} options={[{ v: '', label: t('sl.ct.noStage'), rm: true }, ...stageOpts]} onPick={(x) => pool.setField([it], { sales_stage: x || null })} />
              </div>
            </div>
          );
        })}
      </div>
      {!fl.length && !loading && <div style={{ padding: 24, textAlign: 'center', border: `1px dashed ${C.border}`, borderRadius: 8, font: font(400, 12), color: C.muted }}>{all.length ? t('sl.ct.noMatch') : t('sl.ct.empty')}</div>}
      {fl.length > shown && <Btn kind="ghost" onClick={() => setShown((n) => n + SHOW)} style={{ alignSelf: 'center' }}>{t('sl.ct.more', { count: fl.length - shown })}</Btn>}
      {gate.dialog}
      {view && <VaultViewer accountId={accountId} vault={asVault(view.v, (pool.items[view.v.id] ?? []).length)} initialSel={view.id} onClose={() => setView(null)} onChanged={() => {}} onCredits={() => {}} />}
    </div>
  );
}
